import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  DispatchLocationPreset,
  DispatchLocationPresetCreateInput,
  DeletedDispatch,
  DispatchDeletePreview,
  DispatchDeleteResult,
  DispatchMonthlyBatchOption,
  DispatchRequestAcceptInput,
  DispatchRequestCancelInput,
  DispatchRequestCreateInput,
  DispatchRequestDto,
  DispatchRequestFulfillInput,
  DispatchRequestRejectInput,
  DispatchRequestReturnToDraftInput,
  DispatchRequestRouteInput,
  DispatchRequestStatus,
  DispatchRequestUpdateInput,
  DispatchRecentVehicle,
  FacilityDto,
} from './types';

export interface DispatchListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface DispatchListParams {
  status?: DispatchRequestStatus;
  page?: number;
  per_page?: number;
  monthly_batch_id?: string;
  requested_date_from?: string;
  requested_date_to?: string;
}

export interface DispatchListResult {
  data: DispatchRequestDto[];
  meta: DispatchListMeta;
}

export interface DeletedDispatchListResult {
  data: DeletedDispatch[];
  meta: DispatchListMeta;
}

export interface DispatchLocationPresetParams {
  supplier_company_id?: string;
  q?: string;
  per_page?: number;
}

export interface DispatchRecentVehicleParams {
  facility_id: string;
  limit?: number;
}

export interface DispatchFacilityParams {
  active_only?: boolean;
  company_party_type?: 'supplier' | 'processor' | 'platform';
  facility_role?: 'receiving' | 'shipping' | 'dismantling' | 'storage' | 'admin';
  q?: string;
}

interface FacilityApiDto extends Omit<FacilityDto, 'display_name'> {
  display_name?: string;
}

// ---------------------------------------------------------------------------
// Supplier endpoints — `/api/supplier/...`
// ---------------------------------------------------------------------------

const defaultDispatchMeta = (params: DispatchListParams, dataLength: number): DispatchListMeta => ({
  total: dataLength,
  page: params.page ?? 1,
  per_page: params.per_page ?? dataLength,
});

export const fetchSupplierDispatchRequests = async (
  params: DispatchListParams = {},
): Promise<DispatchListResult> => {
  const res = await apiClient.get<ApiSuccess<DispatchRequestDto[], DispatchListMeta>>(
    '/supplier/dispatch-requests',
    { params },
  );
  const data = res.data.data ?? [];
  return { data, meta: res.data.meta ?? defaultDispatchMeta(params, data.length) };
};

export const fetchDispatchRequests = async (
  params: DispatchListParams = {},
): Promise<DispatchListResult> => {
  const res = await apiClient.get<ApiSuccess<DispatchRequestDto[], DispatchListMeta>>(
    '/dispatch-requests',
    { params },
  );
  const data = res.data.data ?? [];
  return { data, meta: res.data.meta ?? defaultDispatchMeta(params, data.length) };
};

export const createSupplierDispatchRequest = async (
  body: DispatchRequestCreateInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    '/supplier/dispatch-requests',
    body,
  );
  return res.data.data;
};

export const updateSupplierDispatchRequest = async (
  id: string,
  body: DispatchRequestUpdateInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.patch<ApiSuccess<DispatchRequestDto>>(
    `/supplier/dispatch-requests/${id}`,
    body,
  );
  return res.data.data;
};

export const updateProcessorDispatchRequest = async (
  id: string,
  body: DispatchRequestUpdateInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.patch<ApiSuccess<DispatchRequestDto>>(`/dispatch-requests/${id}`, body);
  return res.data.data;
};

export const submitSupplierDispatchRequest = async (
  id: string,
  ccEmails: string[] = [],
): Promise<DispatchRequestDto> => {
  const res = ccEmails.length
    ? await apiClient.post<ApiSuccess<DispatchRequestDto>>(`/supplier/dispatch-requests/${id}/submit`, { cc_emails: ccEmails })
    : await apiClient.post<ApiSuccess<DispatchRequestDto>>(`/supplier/dispatch-requests/${id}/submit`);
  return res.data.data;
};

export const cancelSupplierDispatchRequest = async (
  id: string,
  body: DispatchRequestCancelInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/supplier/dispatch-requests/${id}/cancel`,
    body,
  );
  return res.data.data;
};

export const fetchDispatchLocationPresets = async (
  params: DispatchLocationPresetParams = {},
): Promise<DispatchLocationPreset[]> => {
  const res = await apiClient.get<ApiSuccess<DispatchLocationPreset[]>>(
    '/supplier/dispatch-requests/locations',
    { params },
  );
  return res.data.data ?? [];
};

export const saveDispatchLocationPreset = async (
  body: DispatchLocationPresetCreateInput,
): Promise<DispatchLocationPreset> => {
  const res = await apiClient.post<ApiSuccess<DispatchLocationPreset>>(
    '/supplier/dispatch-requests/locations',
    body,
  );
  return res.data.data;
};

// ---------------------------------------------------------------------------
// Platform endpoints — `/api/platform/...`
// ---------------------------------------------------------------------------

export const fetchPlatformDispatchRequests = async (
  params: DispatchListParams = {},
): Promise<DispatchListResult> => {
  const res = await apiClient.get<ApiSuccess<DispatchRequestDto[], DispatchListMeta>>(
    '/platform/dispatch-requests',
    { params },
  );
  const data = res.data.data ?? [];
  return { data, meta: res.data.meta ?? defaultDispatchMeta(params, data.length) };
};

export const routeDispatchRequest = async (
  id: string,
  body: DispatchRequestRouteInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/platform/dispatch-requests/${id}/route`,
    body,
  );
  return res.data.data;
};

export const rejectPlatformDispatchRequest = async (
  id: string,
  body: DispatchRequestRejectInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/platform/dispatch-requests/${id}/reject`,
    body,
  );
  return res.data.data;
};

export const returnDispatchRequestToDraft = async (
  id: string,
  body: DispatchRequestReturnToDraftInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/dispatch-requests/${id}/return-to-draft`,
    body,
  );
  return res.data.data;
};

export const previewDispatchDelete = async (id: string): Promise<DispatchDeletePreview> => {
  const res = await apiClient.get<ApiSuccess<DispatchDeletePreview>>(
    `/dispatch-requests/${id}/delete-preview`,
  );
  return res.data.data;
};

export const deleteDispatchChain = async (
  id: string,
  body: { reason: string },
): Promise<DispatchDeleteResult> => {
  const res = await apiClient.post<ApiSuccess<DispatchDeleteResult>>(
    `/dispatch-requests/${id}/delete`,
    body,
  );
  return res.data.data;
};

export const fetchDeletedDispatches = async (
  params: Pick<DispatchListParams, 'page' | 'per_page'> = {},
): Promise<DeletedDispatchListResult> => {
  const res = await apiClient.get<ApiSuccess<DeletedDispatch[], DispatchListMeta>>(
    '/dispatch-requests/deleted',
    {
      params,
    },
  );
  const data = res.data.data ?? [];
  return { data, meta: res.data.meta ?? defaultDispatchMeta(params, data.length) };
};

export const restoreDeletedDispatch = async (archiveId: string): Promise<DispatchDeleteResult> => {
  const res = await apiClient.post<ApiSuccess<DispatchDeleteResult>>(
    `/dispatch-requests/deleted/${archiveId}/restore`,
  );
  return res.data.data;
};

// ---------------------------------------------------------------------------
// Facility endpoints — `/api/facilities/:id/...` + `/api/dispatch-requests/:id/...`
// ---------------------------------------------------------------------------

export const fetchFacilityDispatchRequests = async (
  facilityId: string,
  params: DispatchListParams = {},
): Promise<DispatchListResult> => {
  const res = await apiClient.get<ApiSuccess<DispatchRequestDto[], DispatchListMeta>>(
    `/facilities/${facilityId}/dispatch-requests`,
    { params },
  );
  const data = res.data.data ?? [];
  return { data, meta: res.data.meta ?? defaultDispatchMeta(params, data.length) };
};

export const acceptDispatchRequest = async (
  id: string,
  body: DispatchRequestAcceptInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/dispatch-requests/${id}/accept`,
    body,
  );
  return res.data.data;
};

/**
 * GET /api/dispatch-requests/recent-vehicles — plates the accepting facility
 * used most recently, newest first. Gated by `dispatch.request.accept`.
 */
export const fetchRecentDispatchVehicles = async (
  params: DispatchRecentVehicleParams,
): Promise<DispatchRecentVehicle[]> => {
  const res = await apiClient.get<ApiSuccess<DispatchRecentVehicle[]>>(
    '/dispatch-requests/recent-vehicles',
    { params },
  );
  return res.data.data ?? [];
};

export const rejectFacilityDispatchRequest = async (
  id: string,
  body: DispatchRequestRejectInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/dispatch-requests/${id}/reject`,
    body,
  );
  return res.data.data;
};

export const fulfillDispatchRequest = async (
  id: string,
  body: DispatchRequestFulfillInput,
): Promise<DispatchRequestDto> => {
  const res = await apiClient.post<ApiSuccess<DispatchRequestDto>>(
    `/dispatch-requests/${id}/fulfill`,
    body,
  );
  return res.data.data;
};

// ---------------------------------------------------------------------------
// Helper fetches for route dialog dropdowns.
// ---------------------------------------------------------------------------

/**
 * GET /api/facilities — minimal list used by the route dialog. BE may or may
 * not have shipped this yet; we tolerate a missing endpoint by returning [].
 */
export const fetchFacilities = async (
  params: DispatchFacilityParams = {},
): Promise<FacilityDto[]> => {
  try {
    const res = await apiClient.get<ApiSuccess<FacilityApiDto[]>>('/facilities', { params });
    return (res.data.data ?? []).map((row) => ({
      ...row,
      display_name:
        row.display_name ?? row.facility_name ?? row.company_display_name ?? row.facility_id,
    }));
  } catch {
    return [];
  }
};

/** GET /api/monthly-batches — reused from MonthlyBatchesPage's inline query. */
export const fetchMonthlyBatchOptions = async (): Promise<DispatchMonthlyBatchOption[]> => {
  const res = await apiClient.get<ApiSuccess<DispatchMonthlyBatchOption[]>>('/monthly-batches');
  return res.data.data ?? [];
};
